import React from 'react';
import { useAccessibility } from '../context/AccessibilityContext';
import {
  FiX,
  FiEye,
  FiType,
  FiZoomIn,
  FiZoomOut,
  FiMousePointer,
  FiRotateCcw,
  FiMaximize2, // eslint-disable-line no-unused-vars
  FiSun,
  FiTarget,
  FiBookOpen,
  FiVolume2, // eslint-disable-line no-unused-vars
  FiSettings,
  FiPause,
  FiMinus,
  FiPlus // eslint-disable-line no-unused-vars
} from 'react-icons/fi';
import '../styles/AccessibilityModal.css';

/**
 * Accessibility Modal Component
 *
 * A comprehensive accessibility tools interface that provides users with various options
 * to customize their experience and improve application usability. This modal offers
 * visual adjustments, navigation enhancements, zoom controls, and cursor customization
 * to accommodate different accessibility needs and preferences.
 *
 * Key Features:
 * - Visual Adjustments: High contrast mode, large text, dyslexic-friendly fonts, reduced motion
 * - Navigation & Focus: Enhanced focus indicators and reading guides for better navigation
 * - Zoom & Magnification: Customizable zoom levels from 50% to 200% with visual controls
 * - Cursor & Pointer: Multiple cursor sizes (normal, large, extra-large) for better visibility
 * - Settings Persistence: All accessibility settings are automatically saved to localStorage
 * - Keyboard Shortcuts: Comprehensive keyboard shortcuts for quick access to features
 * - Reset Functionality: One-click reset to restore all settings to defaults
 *
 * Accessibility Features:
 * - Proper ARIA labels and roles for screen reader compatibility
 * - Keyboard navigation support with focus management
 * - High contrast themes and large text options
 * - Reduced motion settings for users sensitive to animations
 * - Reading guides to help users track text lines
 * - Enhanced focus indicators for keyboard navigation
 *
 * State Management:
 * - Integrates with AccessibilityContext for global state management
 * - Settings are persisted across browser sessions
 * - Real-time application of accessibility changes
 * - Context provides toggle functions and current state values
 *
 * User Interactions:
 * - Click or keyboard interaction to toggle features
 * - Visual feedback with active/inactive states
 * - Immediate application of changes to the interface
 * - Modal can be closed via close button, backdrop click, or Escape key
 *
 * @returns {JSX.Element|null} The rendered accessibility modal or null if not open
 */
const AccessibilityModal = () => {
  const {
    isModalOpen,
    closeModal,
    highContrast,
    largeText,
    dyslexicFont,
    reducedMotion,
    focusIndicators,
    readingGuide,
    cursorSize,
    zoomLevel,
    toggleHighContrast,
    toggleLargeText,
    toggleDyslexicFont,
    toggleReducedMotion,
    toggleFocusIndicators,
    toggleReadingGuide,
    setCursor,
    setZoom,
    resetAllSettings
  } = useAccessibility();

  if (!isModalOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const zoomIn = () => {
    if (zoomLevel < 200) {
      setZoom(zoomLevel + 10);
    }
  };

  const zoomOut = () => {
    if (zoomLevel > 50) {
      setZoom(zoomLevel - 10);
    }
  };

  return (
    <div className="accessibility-modal-overlay" onClick={handleOverlayClick}>
      <div className="accessibility-modal" role="dialog" aria-labelledby="accessibility-modal-title" aria-modal="true">
        <div className="accessibility-modal-header">
          <h2 id="accessibility-modal-title" className="accessibility-modal-title">
            <FiEye className="accessibility-modal-icon" />
            Accessibility Tools
          </h2>
          <button
            className="accessibility-modal-close"
            onClick={closeModal}
            aria-label="Close accessibility tools"
          >
            <FiX />
          </button>
        </div>

        <div className="accessibility-modal-content">
          {/* Visual Adjustments */}
          <div className="accessibility-section">
            <h3 className="accessibility-section-title">
              <FiSun className="section-icon" />
              Visual Adjustments
            </h3>
            <div className="accessibility-tools-grid">
              <button
                className={`accessibility-tool ${highContrast ? 'active' : ''}`}
                onClick={toggleHighContrast}
                aria-pressed={highContrast}
                title="Toggle high contrast mode for better visibility"
              >
                <FiTarget className="tool-icon" />
                <span className="tool-label">High Contrast</span>
                <span className="tool-status">{highContrast ? 'ON' : 'OFF'}</span>
              </button>

              <button
                className={`accessibility-tool ${largeText ? 'active' : ''}`}
                onClick={toggleLargeText}
                aria-pressed={largeText}
                title="Increase text size for better readability"
              >
                <FiType className="tool-icon" />
                <span className="tool-label">Large Text</span>
                <span className="tool-status">{largeText ? 'ON' : 'OFF'}</span>
              </button>

              <button
                className={`accessibility-tool ${dyslexicFont ? 'active' : ''}`}
                onClick={toggleDyslexicFont}
                aria-pressed={dyslexicFont}
                title="Use dyslexia-friendly font"
              >
                <FiBookOpen className="tool-icon" />
                <span className="tool-label">Dyslexic Font</span>
                <span className="tool-status">{dyslexicFont ? 'ON' : 'OFF'}</span>
              </button>

              <button
                className={`accessibility-tool ${reducedMotion ? 'active' : ''}`}
                onClick={toggleReducedMotion}
                aria-pressed={reducedMotion}
                title="Reduce animations and motion effects"
              >
                <FiPause className="tool-icon" />
                <span className="tool-label">Reduced Motion</span>
                <span className="tool-status">{reducedMotion ? 'ON' : 'OFF'}</span>
              </button>
            </div>
          </div>

          {/* Navigation & Focus */}
          <div className="accessibility-section">
            <h3 className="accessibility-section-title">
              <FiTarget className="section-icon" />
              Navigation & Focus
            </h3>
            <div className="accessibility-tools-grid">
              <button
                className={`accessibility-tool ${focusIndicators ? 'active' : ''}`}
                onClick={toggleFocusIndicators}
                aria-pressed={focusIndicators}
                title="Enhanced focus indicators for keyboard navigation"
              >
                <FiTarget className="tool-icon" />
                <span className="tool-label">Focus Indicators</span>
                <span className="tool-status">{focusIndicators ? 'ON' : 'OFF'}</span>
              </button>

              <button
                className={`accessibility-tool ${readingGuide ? 'active' : ''}`}
                onClick={toggleReadingGuide}
                aria-pressed={readingGuide}
                title="Reading guide to help track text lines"
              >
                <FiMinus className="tool-icon" />
                <span className="tool-label">Reading Guide</span>
                <span className="tool-status">{readingGuide ? 'ON' : 'OFF'}</span>
              </button>
            </div>
          </div>


          {/* Zoom & Magnification */}
          <div className="accessibility-section">
            <h3 className="accessibility-section-title">
              <FiZoomIn className="section-icon" />
              Zoom & Magnification
            </h3>
            <div className="accessibility-zoom-controls">
              <button
                className="accessibility-zoom-btn"
                onClick={zoomOut}
                disabled={zoomLevel <= 50}
                aria-label="Zoom out"
                title="Decrease zoom level"
              >
                <FiZoomOut />
              </button>
              <div className="accessibility-zoom-display">
                <span className="zoom-level">{zoomLevel}%</span>
                <div className="zoom-bar">
                  <div 
                    className="zoom-fill" 
                    style={{ width: `${((zoomLevel - 50) / 150) * 100}%` }}
                  ></div>
                </div>
              </div>
              <button
                className="accessibility-zoom-btn"
                onClick={zoomIn}
                disabled={zoomLevel >= 200}
                aria-label="Zoom in"
                title="Increase zoom level"
              >
                <FiZoomIn />
              </button>
            </div>
          </div>

          {/* Cursor & Pointer */}
          <div className="accessibility-section">
            <h3 className="accessibility-section-title">
              <FiMousePointer className="section-icon" />
              Cursor & Pointer
            </h3>
            <div className="accessibility-cursor-options">
              <button
                className={`accessibility-cursor-option ${cursorSize === 'normal' ? 'active' : ''}`}
                onClick={() => setCursor('normal')}
                aria-pressed={cursorSize === 'normal'}
              >
                Normal
              </button>
              <button
                className={`accessibility-cursor-option ${cursorSize === 'large' ? 'active' : ''}`}
                onClick={() => setCursor('large')}
                aria-pressed={cursorSize === 'large'}
              >
                Large
              </button>
              <button
                className={`accessibility-cursor-option ${cursorSize === 'extra-large' ? 'active' : ''}`}
                onClick={() => setCursor('extra-large')}
                aria-pressed={cursorSize === 'extra-large'}
              >
                Extra Large
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="accessibility-section">
            <h3 className="accessibility-section-title">
              <FiSettings className="section-icon" />
              Quick Actions
            </h3>
            <div className="accessibility-quick-actions">
              <button
                className="accessibility-reset-btn"
                onClick={resetAllSettings}
                title="Reset all accessibility settings to default"
              >
                <FiRotateCcw className="tool-icon" />
                Reset All Settings
              </button>
            </div>
          </div>

          {/* Keyboard Shortcuts */}
          <div className="accessibility-section">
            <h3 className="accessibility-section-title">Keyboard Shortcuts</h3>
            <div className="accessibility-shortcuts">
              <div className="shortcut-item">
                <kbd>Alt + A</kbd>
                <span>Open accessibility tools</span>
              </div>
              <div className="shortcut-item">
                <kbd>Alt + H</kbd>
                <span>Toggle high contrast</span>
              </div>
              <div className="shortcut-item">
                <kbd>Alt + T</kbd>
                <span>Toggle large text</span>
              </div>
              <div className="shortcut-item">
                <kbd>Alt + R</kbd>
                <span>Toggle reading guide</span>
              </div>
              <div className="shortcut-item">
                <kbd>Alt + +</kbd>
                <span>Zoom in</span>
              </div>
              <div className="shortcut-item">
                <kbd>Alt + -</kbd>
                <span>Zoom out</span>
              </div>
            </div>
          </div>
        </div>

        <div className="accessibility-modal-footer">
          <p className="accessibility-modal-description">
            These tools help make the application more accessible. Settings are automatically saved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityModal;